import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Grid,
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
  getActiveGamesRaffles,
  createAgencyOrder,
} from "../../../api/agency-orders/AgencyOrdersService";
import { getOrganizationId } from "../../../utils/session.utils";
import Swal from "sweetalert2";
import styles from "./index.module.css";

export const NuevoPedido = () => {
  const navigate = useNavigate();
  const agencySelected = useSelector(
    (state) => state.myAgencies?.selected_agencie_to_consult,
  );
  const userAgencies = useSelector(
    (state) => state.myAgencies?.user_agencies || [],
  );
  const session = useSelector((state) => state.session);
  const colors = useSelector((state) => state.local.uiConfig);

  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState(
    agencySelected?.id || null,
  );
  const [observations, setObservations] = useState("");
  const [details, setDetails] = useState([
    {
      gameId: "",
      raffleId: "",
      quantityRequested: "",
      observations: "",
    },
  ]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Si hay una agencia seleccionada pero no está en el estado local, usarla
    if (agencySelected?.id && !selectedAgencyId) {
      setSelectedAgencyId(agencySelected.id);
    }
    // Si hay agencias disponibles y no hay ninguna seleccionada, usar la primera
    else if (userAgencies.length > 0 && !selectedAgencyId) {
      setSelectedAgencyId(userAgencies[0].id);
    }
  }, [agencySelected, userAgencies, selectedAgencyId]);

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    setLoadingGames(true);
    try {
      const organizationId = getOrganizationId();
      const response = await getActiveGamesRaffles(organizationId);
      setGames(response.games || []);
    } catch (error) {
      console.error("Error loading games:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los juegos y sorteos. Intente nuevamente.",
      });
    } finally {
      setLoadingGames(false);
    }
  };

  const getRafflesForGame = (gameId) => {
    const game = games.find((g) => g.gameId === parseInt(gameId));
    return game?.raffles || [];
  };

  const handleAddDetail = () => {
    setDetails([
      ...details,
      {
        gameId: "",
        raffleId: "",
        quantityRequested: "",
        observations: "",
      },
    ]);
  };

  const handleRemoveDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
      // Limpiar errores del detalle eliminado
      const newErrors = { ...errors };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(`detail-${index}`)) {
          delete newErrors[key];
        }
      });
      setErrors(newErrors);
    }
  };

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };

    // Si cambia el juego, resetear el sorteo
    if (field === "gameId") {
      newDetails[index].raffleId = "";
      newDetails[index].quantityRequested = "";
    }

    setDetails(newDetails);

    // Validar en tiempo real si cambia cantidad y ya hay juego y sorteo seleccionados
    if (field === "quantityRequested" && newDetails[index].gameId && newDetails[index].raffleId) {
      const quantity = parseInt(value);
      const raffles = getRafflesForGame(newDetails[index].gameId);
      const raffle = raffles.find((r) => r.raffleId === parseInt(newDetails[index].raffleId));
      
      const newErrors = { ...errors };
      const errorKey = `detail-${index}-${field}`;
      
      if (raffle?.isInstantaneo && raffle?.couponsPerPackage && value && quantity > 0) {
        if (quantity % raffle.couponsPerPackage !== 0) {
          const packagesNeeded = Math.ceil(quantity / raffle.couponsPerPackage);
          const correctAmount = packagesNeeded * raffle.couponsPerPackage;
          newErrors[errorKey] = `⚠️ La cantidad debe ser múltiplo de ${raffle.couponsPerPackage} cupones por paquete. Cantidad sugerida: ${correctAmount} (${packagesNeeded} paquetes)`;
        } else {
          delete newErrors[errorKey];
        }
      } else {
        delete newErrors[errorKey];
      }
      
      setErrors(newErrors);
    } else {
      // Limpiar errores del campo modificado
      const errorKey = `detail-${index}-${field}`;
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const validateDetail = (detail, index) => {
    const newErrors = { ...errors };
    let hasError = false;

    if (!detail.gameId) {
      newErrors[`detail-${index}-gameId`] = "El juego es obligatorio";
      hasError = true;
    }

    if (!detail.raffleId) {
      newErrors[`detail-${index}-raffleId`] = "El sorteo es obligatorio";
      hasError = true;
    }

    const quantity = parseInt(detail.quantityRequested);
    if (!detail.quantityRequested || quantity <= 0) {
      newErrors[`detail-${index}-quantityRequested`] =
        "La cantidad debe ser mayor a 0";
      hasError = true;
    }

    // Validar múltiplos para instantáneos
    if (detail.gameId && detail.raffleId && detail.quantityRequested) {
      const raffles = getRafflesForGame(detail.gameId);
      const raffle = raffles.find((r) => r.raffleId === parseInt(detail.raffleId));
      if (raffle?.isInstantaneo && raffle?.couponsPerPackage) {
        if (quantity % raffle.couponsPerPackage !== 0) {
          const packagesNeeded = Math.ceil(quantity / raffle.couponsPerPackage);
          const correctAmount = packagesNeeded * raffle.couponsPerPackage;
          newErrors[`detail-${index}-quantityRequested`] = `⚠️ La cantidad debe ser múltiplo de ${raffle.couponsPerPackage} cupones por paquete. Cantidad sugerida: ${correctAmount} (${packagesNeeded} paquetes)`;
          hasError = true;
        }
      }
    }

    if (detail.observations && detail.observations.length > 500) {
      newErrors[`detail-${index}-observations`] =
        "Las observaciones no pueden exceder 500 caracteres";
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const hasValidationErrors = () => {
    // Validar observaciones generales
    if (observations.length > 500) {
      return true;
    }

    // Validar agencia seleccionada
    if (!selectedAgencyId) {
      return true;
    }

    // Validar cada detalle
    let hasErrors = false;
    details.forEach((detail, index) => {
      const quantity = parseInt(detail.quantityRequested);
      
      // Validaciones básicas
      if (!detail.gameId || !detail.raffleId || !detail.quantityRequested || quantity <= 0) {
        hasErrors = true;
      }

      // Validar múltiplos para instantáneos
      if (detail.gameId && detail.raffleId && detail.quantityRequested) {
        const raffles = getRafflesForGame(detail.gameId);
        const raffle = raffles.find((r) => r.raffleId === parseInt(detail.raffleId));
        if (raffle?.isInstantaneo && raffle?.couponsPerPackage) {
          if (quantity % raffle.couponsPerPackage !== 0) {
            hasErrors = true;
          }
        }
      }

      // Validar observaciones
      if (detail.observations && detail.observations.length > 500) {
        hasErrors = true;
      }
    });

    return hasErrors;
  };

  const validateForm = () => {
    if (observations.length > 500) {
      Swal.fire({
        icon: "warning",
        title: "Error de validación",
        text: "Las observaciones generales no pueden exceder 500 caracteres",
      });
      return false;
    }

    if (!selectedAgencyId) {
      setErrors({ ...errors, agencyId: "La agencia es obligatoria" });
      return false;
    }

    let allValid = true;
    details.forEach((detail, index) => {
      if (!validateDetail(detail, index)) {
        allValid = false;
      }
    });

    if (!allValid) {
      Swal.fire({
        icon: "warning",
        title: "Error de validación",
        text: "Por favor, corrija los errores en los detalles del pedido. Para juegos instantáneos, la cantidad debe ser múltiplo de cupones por paquete.",
      });
    }

    return allValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedAgencyId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Por favor, seleccione una agencia",
      });
      setErrors({ ...errors, agencyId: "La agencia es obligatoria" });
      return;
    }

    setLoadingSubmit(true);
    try {
      const payload = {
        requestedBy: session?.user?.email || session?.decodedToken?.email || "",
        observations: observations || null,
        details: details.map((detail) => ({
          gameId: parseInt(detail.gameId),
          raffleId: parseInt(detail.raffleId),
          quantityRequested: parseInt(detail.quantityRequested),
          observations: detail.observations || null,
        })),
      };

      const response = await createAgencyOrder(selectedAgencyId, payload);
      
      Swal.fire({
        icon: "success",
        title: "Pedido creado exitosamente",
        text: `El pedido #${response.nroOrder} ha sido creado correctamente`,
        confirmButtonText: "Ver detalle",
        showCancelButton: true,
        cancelButtonText: "Volver al listado",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(`/main/pedidos/${response.id}`);
        } else {
          navigate("/main/pedidos");
        }
      });
    } catch (error) {
      console.error("Error creating order:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error al crear el pedido. Verifique los datos ingresados.";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (userAgencies.length === 0) {
    return (
      <Grid container sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          No hay agencias disponibles. Por favor, contacte al administrador.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/main/pedidos")}
          sx={{ mt: 2 }}
        >
          Volver al Listado
        </Button>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigate("/main/pedidos")}>
              <ArrowBackIcon
                sx={{
                  fontSize: "1.8rem",
                  color: colors?.primary?.main || "primary.main",
                }}
              />
            </IconButton>
            <ShoppingCartIcon
              sx={{
                fontSize: "2rem",
                color: colors?.primary?.main || "primary.main",
              }}
            />
            <Typography variant="h4" component="h1">
              Crear Nuevo Pedido
            </Typography>
          </Box>
        </Grid>

        {/* Selección de Agencia */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información General
            </Typography>
            <FormControl fullWidth error={!!errors.agencyId} sx={{ mt: 2 }}>
              <InputLabel>Agencia *</InputLabel>
              <Select
                value={selectedAgencyId || ""}
                label="Agencia *"
                onChange={(e) => {
                  setSelectedAgencyId(e.target.value);
                  const newErrors = { ...errors };
                  delete newErrors.agencyId;
                  setErrors(newErrors);
                }}
              >
                {userAgencies.map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>
                    {agency.number}
                    {agency.subNumber ? ` - ${agency.subNumber}` : ""} - {agency.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.agencyId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.agencyId}
                </Typography>
              )}
            </FormControl>
          </Paper>
        </Grid>

        {/* Observaciones Generales */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Observaciones Generales
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Observaciones (opcional)"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              helperText={`${observations.length}/500 caracteres`}
              error={observations.length > 500}
            />
          </Paper>
        </Grid>

        {/* Detalles del Pedido */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6">Detalles del Pedido</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddDetail}
                disabled={loadingGames}
              >
                Agregar Detalle
              </Button>
            </Box>

            {loadingGames ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              details.map((detail, index) => {
                const raffles = getRafflesForGame(detail.gameId);
                const selectedRaffle = raffles.find(
                  (r) => r.raffleId === parseInt(detail.raffleId),
                );

                return (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Detalle {index + 1}
                      </Typography>
                      {details.length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveDetail(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth error={!!errors[`detail-${index}-gameId`]}>
                          <InputLabel>Juego *</InputLabel>
                          <Select
                            value={detail.gameId}
                            label="Juego *"
                            onChange={(e) =>
                              handleDetailChange(index, "gameId", e.target.value)
                            }
                          >
                            {games.map((game) => (
                              <MenuItem key={game.gameId} value={game.gameId}>
                                {game.gameName}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[`detail-${index}-gameId`] && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {errors[`detail-${index}-gameId`]}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl
                          fullWidth
                          disabled={!detail.gameId}
                          error={!!errors[`detail-${index}-raffleId`]}
                        >
                          <InputLabel>Sorteo *</InputLabel>
                          <Select
                            value={detail.raffleId}
                            label="Sorteo *"
                            onChange={(e) =>
                              handleDetailChange(index, "raffleId", e.target.value)
                            }
                          >
                            {raffles.map((raffle) => (
                              <MenuItem key={raffle.raffleId} value={raffle.raffleId}>
                                #{raffle.raffleNumber} - {raffle.serie}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[`detail-${index}-raffleId`] && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                              {errors[`detail-${index}-raffleId`]}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Cantidad Solicitada *"
                          value={detail.quantityRequested}
                          onChange={(e) =>
                            handleDetailChange(
                              index,
                              "quantityRequested",
                              e.target.value,
                            )
                          }
                          error={!!errors[`detail-${index}-quantityRequested`]}
                          helperText={
                            errors[`detail-${index}-quantityRequested`] ||
                            (selectedRaffle?.isInstantaneo &&
                              selectedRaffle?.couponsPerPackage &&
                              `⚠️ Para juegos instantáneos debe ser múltiplo de ${selectedRaffle.couponsPerPackage} cupones por paquete`)
                          }
                          inputProps={{ min: 1 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Observaciones (opcional)"
                          value={detail.observations}
                          onChange={(e) =>
                            handleDetailChange(index, "observations", e.target.value)
                          }
                          error={!!errors[`detail-${index}-observations`]}
                          helperText={
                            errors[`detail-${index}-observations`] ||
                            `${detail.observations.length}/500 caracteres`
                          }
                        />
                      </Grid>
                    </Grid>

                    {index < details.length - 1 && <Divider sx={{ mt: 3 }} />}
                  </Box>
                );
              })
            )}
          </Paper>
        </Grid>

        {/* Botones de Acción */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/main/pedidos")}
              disabled={loadingSubmit}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loadingSubmit || loadingGames || hasValidationErrors()}
              sx={{ backgroundColor: colors?.primary?.main }}
            >
              {loadingSubmit ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Guardando...
                </>
              ) : (
                "Guardar Pedido"
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

